import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import { useDispatch } from 'react-redux';
import {
  setReviewCreationCalibrationAndDeliverables,
  setReviewCreationGridDefinition,
  setReviewCreationKey,
  useReviewCreationV1Data,
  AUTO_UPDATE_STATUSES,
} from 'reducers/reviewCreationV1Slice';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import useReviewCreationFormId from '../hooks/useReviewCreationFormId';

const getReviewCycleCalibrationAndDeliverables = ({ reviewCycleId }) =>
  sendRequest({
    url: endpoints.reviewCreationV2.calibrationAndDeliverables,
    credentials: 'include',
    queryParams: { reviewCycleId },
  }).then((res) => ({
    ...res.entity,
    isCalibrationQuestionDisabled: res?.entity?.calibrationQuestion === null,
    isOutcomeQuestionDisabled: res?.entity?.outcomeQuestion === null,
  }));

export const useGetReviewsCalibrationAndDeliverables = () => {
  const dispatch = useDispatch();
  const reviewCycleId = useReviewCreationFormId();

  return useQuery([REACT_QUERY_KEYS.reviewCreationV2.calibrationAndDeliverables, reviewCycleId], () =>
    getReviewCycleCalibrationAndDeliverables({ reviewCycleId }).then((data) => {
      dispatch(setReviewCreationCalibrationAndDeliverables(data));
      dispatch(setReviewCreationGridDefinition(data.gridDefinition));
      return data;
    })
  );
};

const updateCalibrationAndDeliverablesApi = ({ reviewCycleId, ...data }) =>
  sendRequest({
    url: endpoints.reviewCreationV2.updateCalibrationAndDeliverables,
    method: 'PUT',
    body: { reviewCycleId, ...data },
    credentials: 'include',
  }).then((res) => res.entity);

export const useUpdateReviewsCalibrationAndDeliverables = () => {
  const { reviewCycleId } = useReviewCreationV1Data();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: object) => updateCalibrationAndDeliverablesApi({ reviewCycleId, ...data }),
    onSuccess: () => {
      // invalidate the cache
      queryClient.invalidateQueries([REACT_QUERY_KEYS.reviewCreationV2.calibrationAndDeliverables]);
    },
    onError: () => {},
  });
};

export const useInvalidateCalibrationAndDeliverables = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.reviewCreationV2.calibrationAndDeliverables]);
  };
};

const publishCalibrationSettingsApi = ({
  reviewCycleId,
  isManagerPublishAllowed,
}: {
  reviewCycleId: number;
  isManagerPublishAllowed: boolean;
}) =>
  sendRequest({
    url: endpoints.reviewCreationV2.publishCalibrationSettings,
    method: 'PUT',
    body: { reviewCycleId, isManagerPublishAllowed },
    credentials: 'include',
  }).then((res) => res.entity);

export const usePublishCalibrationSettings = () => {
  const { reviewCycleId } = useReviewCreationV1Data();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ isManagerPublishAllowed }: { isManagerPublishAllowed: boolean }) =>
      publishCalibrationSettingsApi({ reviewCycleId, isManagerPublishAllowed }),
    onMutate: () => {
      dispatch(setReviewCreationKey({ autoUpdateStatus: AUTO_UPDATE_STATUSES.IS_SAVING }));
    },
    onSuccess: () => {
      setTimeout(() => dispatch(setReviewCreationKey({ autoUpdateStatus: AUTO_UPDATE_STATUSES.UPDATED })), 500);
    },
    onError: () => {
      dispatch(setReviewCreationKey({ autoUpdateStatus: AUTO_UPDATE_STATUSES.IS_ERROR }));
    },
  });
};
